import type { ApiClient } from '@carbonwebmail/api-client'
import type { InvitationDto, MemberDto, OrgRoleDto } from '@carbonwebmail/contracts'
import { canActOnMember, canGrantRole } from '@carbonwebmail/domain'
import { useCallback, useState } from 'react'
import { Alert } from '../../ui/Alert.js'
import { Async } from '../../ui/Async.js'
import { CreateCard } from '../../ui/CreateCard.js'
import { Field, SelectField } from '../../ui/Field.js'
import { when } from '../../ui/format.js'
import { TableSkeleton } from '../../ui/Pager.js'
import { StatusPill } from '../../ui/StatusPill.js'
import { Table, Td, Tr } from '../../ui/Table.js'
import { useMay, useYourRole } from '../../ui/yourRole.js'

import { useTeam } from './useTeam.js'

/**
 * Who is in this organisation, and who has been asked to join.
 *
 * Both lists are on one screen because they answer one question — who can get
 * in — and a page that showed only accepted members would let an administrator
 * forget a working link sitting in the inbox of somebody who left.
 *
 * Four roles, and the screen says what each one may do rather than leaving
 * somebody to infer it from a word.
 *
 * **What you are offered is what you may actually do.** The options in every
 * selector are filtered through the same `canGrantRole` the server checks, and
 * the buttons on a row appear only when `canActOnMember` says so — so an
 * admin is never shown an "Owner" option that answers 403, and never offered a
 * Remove button for the owner. Hiding is not the control; the route is. This
 * only stops the screen promising something the server will refuse.
 *
 * The organisation cannot be left with nobody who can manage it. That rule is
 * enforced in the database, where two people acting at once cannot slip past
 * it — this screen only explains the refusal.
 */

/** What each role is for, in the words somebody choosing one needs. */
const ROLE_OPTIONS: readonly (readonly [OrgRoleDto, string])[] = [
  ['viewer', 'Viewer — reads everything, changes nothing'],
  ['member', 'Member — contacts, campaigns, and sending'],
  ['admin', 'Administrator — all of that, plus settings and the team'],
  ['owner', 'Owner — everything, including other owners'],
]

const ROLE_LABELS: Record<OrgRoleDto, string> = {
  owner: 'owner',
  admin: 'administrator',
  member: 'member',
  viewer: 'viewer',
}

/** The roles `you` may hand out. The server checks the same thing. */
function grantable(you: OrgRoleDto): readonly (readonly [string, string])[] {
  return ROLE_OPTIONS.filter(([role]) => canGrantRole(you, role))
}

export function TeamPage({ client }: { readonly client: ApiClient }) {
  /** Your own role. Decides what this screen offers, as the server decides what it allows. */
  const you = useYourRole()
  /*
   * Whether to offer the invitation form at all.
   *
   * It used to be shown to everybody, and for a member or a viewer it was a
   * form that could not work: `grantable` filters the roles by what you may
   * hand out, which for them is none, so the role list came up empty and the
   * button answered 403. The list below is worth their seeing — who is in the
   * organisation is not a secret from the people in it — so the page stays and
   * only the form goes.
   */
  const mayManage = useMay('team', 'write')
  const team = useTeam({ client })
  const { state, notice, problem, busy } = team

  const [email, setEmail] = useState('')
  const [role, setRole] = useState<string>('member')

  const send = useCallback(async () => {
    if (await team.invite(email, role as OrgRoleDto)) setEmail('')
  }, [team, email, role])

  return (
    <>
      <header className="mb-6">
        <p className="mt-1 text-[13px] text-t2">
          Everybody who can sign in to this organisation, and everybody who has been asked to.
        </p>
      </header>

      {problem === undefined ? null : <Alert>{problem}</Alert>}
      {notice === undefined ? null : <Alert tone="ok">{notice}</Alert>}

      {mayManage ? (
        <CreateCard
          heading="Invite somebody"
          submit="Send invitation"
          disabled={busy || email.trim() === ''}
          onSubmit={() => void send()}
        >
          <Field
            id="invite-email"
            label="Email address"
            type="email"
            autoComplete="off"
            value={email}
            disabled={busy}
            hint="They get a link. No account exists until they use it."
            onChange={setEmail}
          />
          <SelectField
            id="invite-role"
            label="Role"
            value={role}
            disabled={busy}
            options={grantable(you)}
            onChange={setRole}
          />
        </CreateCard>
      ) : null}

      <Async state={state} skeleton={<TableSkeleton />} onRetry={() => void team.retry()}>
        {(loaded) => (
          <>
            <h2 className="mb-3 font-bold text-[15px] text-t1">Members</h2>
            <Table head={['Name', 'Role', 'Joined', '']} minWidth={760}>
              {loaded.members.map((member) => (
                <MemberRow
                  key={member.userId}
                  member={member}
                  you={you}
                  busy={busy}
                  onSetRole={team.setRole}
                  onRemove={team.remove}
                />
              ))}
            </Table>

            {loaded.invitations.length === 0 ? null : (
              <section className="mt-8">
                <h2 className="mb-3 font-bold text-[15px] text-t1">Invited, not yet joined</h2>
                <Table head={['Address', 'Role', 'Sent', 'Link expires', '']} minWidth={760}>
                  {loaded.invitations.map((invitation) => (
                    <InvitationRow
                      key={invitation.email}
                      invitation={invitation}
                      busy={busy}
                      onRevoke={team.revoke}
                    />
                  ))}
                </Table>
              </section>
            )}
          </>
        )}
      </Async>
    </>
  )
}

function MemberRow({
  member,
  you,
  busy,
  onSetRole,
  onRemove,
}: {
  readonly member: MemberDto
  readonly you: OrgRoleDto
  readonly busy: boolean
  readonly onSetRole: (userId: string, role: OrgRoleDto) => Promise<void>
  readonly onRemove: (userId: string) => Promise<void>
}) {
  return (
    <Tr>
      <Td strong>
        {/* An empty name is not a blank cell: nobody was ever asked for one, and
            the address is the true thing to show instead. */}
        {member.name === '' ? member.email : member.name}
        {member.name === '' ? null : (
          <span className="ml-2 text-[12px] text-t3">{member.email}</span>
        )}
        {member.isYou ? <span className="ml-2 text-[12px] text-t3">(you)</span> : null}
      </Td>
      <Td>
        <StatusPill tone={member.role === 'owner' || member.role === 'admin' ? 'ok' : 'unknown'}>
          {ROLE_LABELS[member.role]}
        </StatusPill>
      </Td>
      <Td>{when(member.joinedAt)}</Td>
      <Td>
        {/*
          Nothing to act on for yourself. Changing your own role or removing
          your own account from this screen is a way to lock yourself out
          without meaning to; the profile screen is where you act on you.

          And nothing to act on for somebody who outranks you: an admin sees
          the owner's row and no buttons on it, which is the truth rather than
          a button that answers 403.
        */}
        {member.isYou || !canActOnMember(you, member.role) ? null : (
          <span className="flex items-center gap-3">
            {/*
              A selector, not a toggle. The old pair of buttons flipped between
              two roles, which is a control that cannot express four — and
              could not have expressed three.
            */}
            <select
              aria-label={`Role for ${member.email}`}
              value={member.role}
              disabled={busy}
              onChange={(event) => void onSetRole(member.userId, event.target.value as OrgRoleDto)}
              className="rounded-field border border-line bg-surface px-2 py-1 text-[13px] text-t1 disabled:opacity-60"
            >
              {grantable(you).map(([role, label]) => (
                <option key={role} value={role}>
                  {label}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={busy}
              onClick={() => void onRemove(member.userId)}
              className="font-medium text-[13px] text-t3 hover:text-danger disabled:opacity-60"
            >
              Remove
            </button>
          </span>
        )}
      </Td>
    </Tr>
  )
}

function InvitationRow({
  invitation,
  busy,
  onRevoke,
}: {
  readonly invitation: InvitationDto
  readonly busy: boolean
  readonly onRevoke: (email: string) => Promise<void>
}) {
  return (
    <Tr>
      <Td strong>{invitation.email}</Td>
      <Td>{ROLE_LABELS[invitation.role]}</Td>
      <Td>{when(invitation.invitedAt)}</Td>
      <Td>{when(invitation.expiresAt)}</Td>
      <Td>
        <button
          type="button"
          disabled={busy}
          onClick={() => void onRevoke(invitation.email)}
          className="font-medium text-[13px] text-t3 hover:text-danger disabled:opacity-60"
        >
          Revoke
        </button>
      </Td>
    </Tr>
  )
}
